import { Link } from "react-router-dom";
import {
  ArrowRight,
  BellRing,
  FileText,
  Truck,
  BarChart3,
  PackageCheck,
  Wallet,
} from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/site/Reveal";

const PROBLEMS = [
  {
    icon: BellRing,
    problem: "Follow-ups slip through the cracks",
    solution: "Smart reminders that nudge your team before a lead goes cold.",
  },
  {
    icon: FileText,
    problem: "Quotes take days to go out",
    solution: "Instant, professional quotes generated from the field in minutes.",
  },
  {
    icon: Truck,
    problem: "\u201cWhere is my truck?\u201d calls all day",
    solution: "Live dispatch tracking your whole team — and customers — can see.",
  },
  {
    icon: BarChart3,
    problem: "End-of-day reports eat your evening",
    solution: "Automatic daily summaries of visits, quotes, deals and dispatches.",
  },
  {
    icon: PackageCheck,
    problem: "Stock surprises at the worst time",
    solution: "Simple stock alerts that warn you before you run out.",
  },
  {
    icon: Wallet,
    problem: "Payment chasing feels endless",
    solution: "Polite, automatic payment reminders that keep cash flowing.",
  },
];

const INDUSTRIES = [
  "RMC & Construction",
  "Retail & Wholesale",
  "Logistics & Fleet",
  "Field Services",
  "Manufacturing",
];

export default function Industries() {
  return (
    <div data-testid="industries-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-14 sm:pt-20 pb-24 sm:pb-28">
        <div className="absolute -right-40 top-24 w-[460px] h-[460px] rounded-full bg-brand-blueSoft blur-3xl opacity-80 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal y={16}>
            <Eyebrow>Industries</Eyebrow>
          </Reveal>
          <h1 className="mt-5 font-display font-black tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl max-w-4xl" data-testid="industries-headline">
            <MaskedLine delay={0.15}>Everyday AI for the</MaskedLine>
            <MaskedLine delay={0.32}>
              <span className="text-brand-red">MSME business owner.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="industries-subtext">
              Not moonshots. YesAI solves the small, daily problems that quietly eat your time — follow-ups, quotes, dispatches, reports — with practical AI that works the way you already do.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-9 flex flex-wrap gap-2.5" data-testid="industries-served-chips">
              {INDUSTRIES.map((ind) => (
                <span
                  key={ind}
                  className="bg-white border border-slate-200 hover:border-brand-red hover:text-brand-red text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 cursor-default"
                >
                  {ind}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROBLEMS -> SOLUTIONS */}
      <section className="py-24 sm:py-28 bg-slate-50" data-testid="problems-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Sound familiar?</Eyebrow>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
              The everyday problems we make disappear.
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.problem} delay={(i % 3) * 0.1}>
                <div
                  data-testid={`problem-card-${i}`}
                  className="group bg-white border border-slate-200/80 rounded-3xl p-8 h-full hover:shadow-xl hover:-translate-y-1 hover:border-brand-red/30 transition-shadow transition-transform duration-300"
                >
                  <span className="w-12 h-12 rounded-2xl bg-brand-redSoft text-brand-red flex items-center justify-center group-hover:bg-brand-red group-hover:text-white transition-colors duration-300">
                    <p.icon className="w-6 h-6" />
                  </span>
                  <h3 className="mt-6 font-display font-bold text-lg tracking-tight text-slate-800">{p.problem}</h3>
                  <div className="mt-4 flex items-start gap-2.5">
                    <ArrowRight className="w-4 h-4 text-brand-red mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600 leading-relaxed">{p.solution}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS STRIP */}
      <section className="py-24 sm:py-28" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 w-full h-full rounded-3xl border-2 border-brand-blue/40 pointer-events-none" />
              <img
                src="https://images.unsplash.com/photo-1753351052617-62818ffc9173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw3fHxzbWFsbCUyMGJ1c2luZXNzJTIwc3RvcmUlMjBvd25lciUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg5NjE2MDk5fDA&ixlib=rb-4.1.0&q=85"
                alt="Small business owner using technology in their store"
                className="rounded-3xl object-cover w-full aspect-[4/3] shadow-2xl"
                loading="lazy"
                data-testid="industries-owner-image"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow>How YesAI works</Eyebrow>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl leading-tight">
                We learn your business, then build your AI.
              </h2>
            </Reveal>
            <div className="mt-10 space-y-8">
              {[
                { num: "01", title: "Tell us your daily grind", text: "A short call about where your hours actually go. That's all we need to start." },
                { num: "02", title: "We build a simple app around it", text: "Custom, practical AI shaped to your workflow — not a generic tool you have to bend around." },
                { num: "03", title: "Your team uses it from day one", text: "Mobile friendly, simple to use, and supported by us while it pays for itself." },
              ].map((s, i) => (
                <Reveal key={s.num} delay={i * 0.12}>
                  <div className="flex gap-6" data-testid={`how-step-${s.num}`}>
                    <p className="font-mono text-brand-red font-semibold text-sm tracking-widest pt-1">{s.num}</p>
                    <div>
                      <h3 className="font-display font-bold text-xl tracking-tight">{s.title}</h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 sm:pb-32 px-5 sm:px-8" data-testid="industries-cta-banner">
        <Reveal>
          <div className="max-w-7xl mx-auto bg-brand-red rounded-[2.5rem] px-8 sm:px-14 py-16 sm:py-20 relative overflow-hidden">
            <div aria-hidden className="absolute -right-12 -top-24 font-display font-black text-[10rem] leading-none text-white/10 select-none pointer-events-none">
              AI
            </div>
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <h2 className="font-display font-extrabold text-white tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Your everyday, made smarter.
                </h2>
                <p className="mt-4 text-white/80 max-w-md text-sm sm:text-base leading-relaxed">
                  Tell us what eats your day. We'll show you the AI that gives it back.
                </p>
              </div>
              <Link
                to="/book-demo"
                data-testid="industries-cta-book-demo-btn"
                className="group inline-flex items-center justify-center gap-2.5 bg-white text-brand-red font-bold px-8 py-4 rounded-full hover:bg-brand-ink hover:text-white transition-colors duration-200 shrink-0"
              >
                Book a Demo
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
